import { useEffect, useMemo, useState } from 'react';
import { get } from '@vigilo/api-client';

export type MapBasemapId = 'dark' | 'satellite' | 'light';

export interface MapBasemapConfig {
  id: MapBasemapId;
  label: string;
  tileUrl: string;
  attribution: string;
  maxZoom: number;
  maxNativeZoom?: number;
  fallback?: {
    tileUrl: string;
    attribution: string;
    maxZoom: number;
  };
}

export interface MapGeocodingConfig {
  providerId: string;
  searchEndpoint: string;
  attribution: string;
  minimumQueryLength: number;
  maxResults: number;
}

interface MapProviderConfig {
  applicationId: string;
  basemaps: MapBasemapConfig[];
  defaultBasemap: MapBasemapId;
  geocoding: MapGeocodingConfig;
}

interface ApiResponse<T> {
  data: T;
}

interface MapProviderConfigState {
  basemaps: Partial<Record<MapBasemapId, MapBasemapConfig>>;
  basemapIds: MapBasemapId[];
  defaultBasemap: MapBasemapId;
  geocoding: MapGeocodingConfig | null;
  loading: boolean;
  error: string | null;
}

export function useMapProviderConfig(applicationId = 'crowd_monitoring'): MapProviderConfigState {
  const [config, setConfig] = useState<MapProviderConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    get<ApiResponse<MapProviderConfig>>(`/geo/map-config?applicationId=${encodeURIComponent(applicationId)}`)
      .then(response => {
        if (!active) return;
        setConfig(response.data);
      })
      .catch(() => {
        if (!active) return;
        setConfig(null);
        setError('Map provider configuration is unavailable.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [applicationId]);

  return useMemo(() => {
    const basemaps: Partial<Record<MapBasemapId, MapBasemapConfig>> = {};
    for (const basemap of config?.basemaps ?? []) {
      basemaps[basemap.id] = basemap;
    }

    const basemapIds = (config?.basemaps ?? []).map(basemap => basemap.id);
    return {
      basemaps,
      basemapIds,
      defaultBasemap: config?.defaultBasemap ?? 'dark',
      geocoding: config?.geocoding ?? null,
      loading,
      error,
    };
  }, [config, loading, error]);
}
